'use client'

import type { EmblaCarouselType } from 'embla-carousel'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { baseUrl } from '@/helpers/urls'
import type { GetProductOutput } from '@/http/zamply/products/get-product-schema'
import type { CourseType } from '@/types/course-type'
import { trackEvent } from '@/components/@analitycs/trackEvent'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import starIcon from '@/public/icons/star.svg'
import { getDeviceInfo } from '@/utils/get-device-info'
import { sessionCookie } from '@/utils/session-cookie'

interface PostgraduateCaroulselProps {
  graduationCourses: CourseType[]
  graduation: GetProductOutput | null
}

export default function PostgraduateCaroulsel({
  graduationCourses,
  graduation,
}: PostgraduateCaroulselProps) {
  const hasTrackedRef = useRef(false)

  const [api, setApi] = useState<EmblaCarouselType | undefined>(undefined)
  const [current, setCurrent] = useState(0)
  const [count, setCount] = useState(0)
  const [referrer, setReferrer] = useState('')

  const session = sessionCookie

  const status = session?.state.status
  const fullName = session?.state.profile?.full_name
  const email = session?.state.profile?.email
  const userId = session?.state.profile?.reference
  const externalId = session?.state.profile?.external_id
  const device = useMemo(() => getDeviceInfo(), [])

  useEffect(() => {
    setReferrer(document.referrer)
  }, [])

  useEffect(() => {
    if (hasTrackedRef.current || !referrer) return

    const eventDataSelectItem = {
      event: 'vitrine',
      eventName: 'select_item',
      customDataObject: {
        environment: 'Vitrine',
        item_course: graduation?.reference ?? graduation?.external_id,
        item_referrer: referrer,
        item_device: device,
        ...(status === 'authenticated' && {
          user_id: userId ?? externalId,
          item_name: fullName,
          item_email: email,
        }),
      },
    }

    const eventDataViewItem = {
      event: 'vitrine',
      eventName: 'view_item',
      customDataObject: {
        environment: 'Vitrine',
        item_course: graduation?.reference ?? graduation?.external_id,
        item_referrer: referrer,
        item_device: device,
        ...(status === 'authenticated' && {
          user_id: userId ?? externalId,
          item_name: fullName,
          item_email: email,
        }),
      },
    }

    // falta id e area do curso

    const eventDataViewContent = {
      event: 'vitrine',
      eventName: 'ViewContent',
      customDataObject: {
        environment: 'Vitrine',
        content_type: 'product',
        value: graduation?.amount,
        content_name: graduation?.name,
        content_ids: [graduation?.reference ?? graduation?.external_id],
        currency: 'BRL',
        items: {
          sku: graduation?.reference ?? graduation?.external_id,
          item_id: graduation?.reference ?? graduation?.external_id,
          name: graduation?.name,
          category: 'Pós-graduação',
          item_category: 'Pós-graduação',
          price: graduation?.amount,
          quantity: 1,
        },
      },
    }

    trackEvent(eventDataSelectItem)
    trackEvent(eventDataViewItem)
    trackEvent(eventDataViewContent)

    hasTrackedRef.current = true
  }, [
    status,
    fullName,
    email,
    device,
    referrer,
    userId,
    graduation,
    externalId,
  ])

  const scrollPrev = useCallback(() => {
    api?.scrollPrev()
  }, [api])

  const scrollNext = useCallback(() => {
    api?.scrollNext()
  }, [api])

  useEffect(() => {
    if (!api) {
      return
    }

    const updateCountAndCurrent = () => {
      setCount(api.scrollSnapList().length)
      setCurrent(api.selectedScrollSnap() + 1)
    }

    updateCountAndCurrent()

    api.on('select', updateCountAndCurrent)

    return () => {
      api.off('select', updateCountAndCurrent)
    }
  }, [api])

  return (
    <div className="">
      <Carousel
        className="max-w-234.5"
        opts={{
          align: 'start',
        }}
        setApi={setApi}
      >
        <div className="mb-8 flex flex-wrap items-end justify-between">
          <div className="mb-4">
            <h1 className="font-medium text-2.5xl text-primary-700 leading-8">
              Nossos alunos também adquiriram
            </h1>
            <p className="mt-2 font-normal text-[#656566] text-sm leading-none">
              Descubra mais cursos para alcançar seus objetivos profissionais e
              acadêmicos.
            </p>
          </div>

          <div className="space-x-2">
            <button
              className={twMerge(
                'rounded-full bg-primary-100 p-2 transition-all duration-200',
                current > 1 && 'bg-primary-300'
              )}
              onClick={scrollPrev}
              type="button"
            >
              <ChevronLeft
                className={
                  current > 1
                    ? 'text-prima100bg-primary-100'
                    : 'text-primabg-primary-300'
                }
              />
            </button>
            <button
              className={twMerge(
                'rounded-full bg-primary-300 p-2 transition-all duration-200',
                current === count && 'bg-primary-100'
              )}
              onClick={scrollNext}
              type="button"
            >
              <ChevronRight
                className={
                  current === count
                    ? 'text-primabg-primary-300'
                    : 'text-prima100bg-primary-100'
                }
              />
            </button>
          </div>
        </div>

        <div>
          <CarouselContent className="flex items-center gap-y-4">
            {graduationCourses.map(course => (
              <CarouselItem
                className="relative basis-auto md:basis-1/3 lg:basis-1/4"
                key={course.reference}
              >
                <Link
                  className="group"
                  href={baseUrl(`/pos-graduacoes/info/${course.external_id}`)}
                >
                  <Image
                    alt={course.name}
                    className="h-100.75 rounded-1xl object-cover"
                    height={403}
                    src={course.image_url ?? ''}
                    width={220}
                  />
                  <div className="group-hover:-translate-y-4 absolute bottom-2.5 left-7 flex h-56 max-w-49 flex-col justify-between rounded-lg bg-white p-4 transition-transform duration-200">
                    <div>
                      <span className="rounded-full bg-primary-300 px-3 py-0.5 text-[#FCFCFC] text-xs">
                        Carga Horária{' '}
                        {course.workloads?.[0]?.workload_value ?? 'N/A'} Horas
                      </span>

                      <h4 className="mt-2 font-normal text-[#2E2E2E] text-base leading-5">
                        {course.name}
                      </h4>
                    </div>

                    <div>
                      <div className="mt-2 flex items-center">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Image
                            alt="Ícone de estrela"
                            height={16}
                            key={i}
                            src={starIcon}
                            width={16}
                          />
                        ))}
                      </div>

                      <button
                        className="mt-2 flex items-center font-semibold text-[#656566] text-sm"
                        type="button"
                      >
                        Acessar
                        <ChevronRight
                          className="size-5 text-[#ABACAD]"
                          strokeWidth={2.5}
                        />
                      </button>
                    </div>
                  </div>
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
        </div>
      </Carousel>
    </div>
  )
}
